// src/pages/events/EventsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Plus,
  MapPin,
  Clock,
  Users,
  Upload,
  FileText,
  CheckCircle,
  X,
  Search,
  Filter,
  Eye,
  Edit2,
  Trash2,
  Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { workloadService } from '../../services/workload.service';
import { Workload, EventMetadata } from '../../types/workload.types';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { toast } from 'react-hot-toast';

const EventsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProject, projects } = useProject();
  const [events, setEvents] = useState<Workload[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'upcoming' | 'past'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  useEffect(() => {
    loadEvents();
  }, [selectedProject]);

  const loadEvents = async () => {
    if (!selectedProject) {
      setLoading(false);
      return;
    }

    try {
      const workloads = await workloadService.getProjectWorkloads(selectedProject.id);
      // Filter to only event type workloads
      const eventWorkloads = workloads.filter(w => w.type === 'event');
      setEvents(eventWorkloads);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const getEventMetadata = (workload: Workload): EventMetadata | null => {
    if (workload.sourceMetadata && 'type' in workload.sourceMetadata && workload.sourceMetadata.type === 'event') {
      return workload.sourceMetadata as EventMetadata;
    }
    return null;
  };

  const filteredEvents = events.filter(event => {
    const metadata = getEventMetadata(event);
    if (!metadata) return false;

    const eventDate = parseISO(metadata.eventDate);
    const now = new Date();

    if (filterStatus === 'upcoming' && isBefore(eventDate, now)) return false;
    if (filterStatus === 'past' && isAfter(eventDate, now)) return false;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        event.name.toLowerCase().includes(searchLower) ||
        metadata.eventName.toLowerCase().includes(searchLower) ||
        metadata.eventLocation.name.toLowerCase().includes(searchLower)
      );
    }

    return true;
  }).sort((a, b) => {
    const metaA = getEventMetadata(a);
    const metaB = getEventMetadata(b);
    if (!metaA || !metaB) return 0;
    return new Date(metaB.eventDate).getTime() - new Date(metaA.eventDate).getTime();
  });

  if (!selectedProject) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Project</h3>
          <p className="text-gray-500">Please select a project to view events.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Events</h1>
          <p className="text-gray-500 mt-1">Manage events and capture leads</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
          >
            <Plus className="h-5 w-5" />
            Create Event
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'upcoming', 'past'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No events found</h3>
          <p className="text-gray-500">
            {isAdmin ? 'Create your first event to get started.' : 'No events scheduled yet.'}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {filteredEvents.map((event) => {
            const metadata = getEventMetadata(event);
            if (!metadata) return null;

            const eventDate = parseISO(metadata.eventDate);
            const isPast = isBefore(eventDate, new Date());

            return (
              <div
                key={event.id}
                className={`bg-white rounded-xl shadow-sm border ${
                  isPast ? 'border-gray-100' : 'border-primary-100'
                } overflow-hidden hover:shadow-md transition-shadow`}
              >
                {/* Event Header */}
                <div className={`p-4 ${isPast ? 'bg-gray-50' : 'bg-gradient-to-r from-primary-50 to-secondary-50'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">{metadata.eventName}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{event.name}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      isPast
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-accent-100 text-accent-700'
                    }`}>
                      {isPast ? 'Past' : 'Upcoming'}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  {/* Event Details */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {format(eventDate, 'EEEE, MMMM d, yyyy')}
                    </div>
                    {metadata.eventStartTime && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="h-4 w-4 text-gray-400" />
                        {metadata.eventStartTime}
                        {metadata.eventEndTime && ` - ${metadata.eventEndTime}`}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="truncate">
                        {metadata.eventLocation.name}, {metadata.eventLocation.city}
                      </span>
                    </div>
                    {metadata.expectedAttendees && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4 text-gray-400" />
                        {metadata.expectedAttendees} expected attendees
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-accent-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {event.progress.leadsGenerated} leads
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {event.progress.surveyResponses} surveys
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors">
                      <Upload className="h-4 w-4" />
                      Upload Leads
                    </button>
                    <button className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          projectId={selectedProject.id}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadEvents();
          }}
        />
      )}
    </div>
  );
};

// Create Event Modal
interface CreateEventModalProps {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ projectId, onClose, onCreated }) => {
  const { currentUser, userProfile } = useAuth();
  const [name, setName] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [locationZip, setLocationZip] = useState('');
  const [eventType, setEventType] = useState('');
  const [expectedAttendees, setExpectedAttendees] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      await workloadService.createWorkload({
        name: name || `Event: ${eventName}`,
        projectId,
        type: 'event',
        territory: {
          name: locationName,
          type: 'polygon'
        },
        sourceMetadata: {
          type: 'event',
          eventName,
          eventDate,
          eventStartTime: startTime || undefined,
          eventEndTime: endTime || undefined,
          eventLocation: {
            name: locationName,
            address: locationAddress,
            city: locationCity,
            state: locationState,
            zip: locationZip
          },
          eventType: eventType || undefined,
          expectedAttendees: expectedAttendees ? parseInt(expectedAttendees) : undefined,
          notes: notes || undefined
        }
      }, currentUser.uid, userProfile.displayName);

      toast.success('Event created successfully');
      onCreated();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Create Event</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="School Open House"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workload Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Leave blank to auto-generate"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select type</option>
                <option value="school_fair">School Fair</option>
                <option value="open_house">Open House</option>
                <option value="community_event">Community Event</option>
                <option value="recruitment">Recruitment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Location</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Venue Name"
              />
              <input
                type="text"
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Street Address"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  value={locationCity}
                  onChange={(e) => setLocationCity(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={locationState}
                  onChange={(e) => setLocationState(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="State"
                />
                <input
                  type="text"
                  value={locationZip}
                  onChange={(e) => setLocationZip(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ZIP"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Attendees</label>
            <input
              type="number"
              value={expectedAttendees}
              onChange={(e) => setExpectedAttendees(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Additional notes..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Event
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventsPage;
